/**
 * Mixin for warehouse document tab components (Entry, Invoice, Return, WriteOff).
 * Extracts common patterns: load(), filtered/grouped documents, export, print, excel watchers.
 *
 * Required from the component:
 *   - data: `documentType` (string) — e.g., "entry", "exit", "return", "write-off"
 *   - computed: `headers` (array)
 *   - computed: `exportTitle` (string)
 *
 * Provides:
 *   - data: documents, loading, aggregateMode
 *   - methods: load(), formatNumber()
 *   - computed: translatedDocuments, filteredDocuments, displayedDocuments, mobileHeaders,
 *               documentsByWarehouse, filters, allAggregatedItems, aggregatedItems,
 *               activeAggregatedItems, aggregateHeaders, exportHeaders, exportItems
 *   - watchers: refreshTrigger, isPrinting, isExcelDownloading
 *
 * WarehouseTabEntry overrides most computed properties — mixin additions won't affect it.
 */
import warehouseService from "@/services/warehouse";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasWarehousePrinting from "@/mixins/warehouse/hasWarehousePrinting";
import { isInDateRange, groupByWarehouse } from "@/utils/warehouseUtils";

export default {
    mixins: [hasPrinting, hasWarehousePrinting, hasExcel, hasSnackbar],
    data() {
        return {
            documents: [],
            loading: false,
            aggregateMode: false,
        };
    },
    computed: {
        lastEntryDate() {
            if (!this.documents.length) return null;
            const sorted = [...this.documents].sort(
                (a, b) => new Date(b.document_date) - new Date(a.document_date)
            );
            return this.localeDate(sorted[0].document_date);
        },
        filters() {
            return this.$store.state.warehouse.filters;
        },
        mobileHeaders() {
            return this.headers.filter((h) => h.mobile !== false);
        },
        translatedDocuments() {
            return this.documents.map((document) => ({
                ...document,
                number: this.translate(document.number),
                notes: this.translate(document.notes),
            }));
        },
        filteredDocuments() {
            return this.customSearch(
                this.translatedDocuments,
                this.headers,
                this.filters.search
            ).filter((item) => {
                const dateFilter = isInDateRange(
                    item,
                    "document_date",
                    this.filters.dates.from,
                    this.filters.dates.to
                );
                const warehouseFilter =
                    this.filters.warehouse.length === 0 ||
                    this.filters.warehouse.some(
                        (warehouse) => warehouse.id === item.warehouse_id
                    );
                return dateFilter && warehouseFilter;
            });
        },
        documentsByWarehouse() {
            return groupByWarehouse(
                this.filteredDocuments,
                this.$t("stock")
            );
        },
        displayedDocuments() {
            if (!this.filters.groceryCode) return this.filteredDocuments;
            return this.filteredDocuments.filter((doc) =>
                (doc.groceries || []).some((g) => g.code === this.filters.groceryCode.code)
            );
        },
        aggregationBaseDocuments() {
            return this.translatedDocuments.filter((item) => {
                const dateFilter = isInDateRange(
                    item,
                    "document_date",
                    this.filters.dates.from,
                    this.filters.dates.to
                );
                const warehouseFilter =
                    this.filters.warehouse.length === 0 ||
                    this.filters.warehouse.some(
                        (warehouse) => warehouse.id === item.warehouse_id
                    );
                return dateFilter && warehouseFilter;
            });
        },
        allAggregatedItems() {
            const groceryMap = {};
            this.aggregationBaseDocuments.forEach((doc) => {
                (doc.groceries || []).forEach((g) => {
                    if (!g.id) return;
                    if (!groceryMap[g.id]) {
                        groceryMap[g.id] = {
                            grocery_name: g.name,
                            grocery_code: g.code,
                            measure: g.measure,
                            total_quantity: 0,
                            total_amount: 0,
                            document_count: 0,
                        };
                    }
                    const entry = groceryMap[g.id];
                    entry.total_quantity += Number(g.quantity) || Number(g.amount) || 0;
                    entry.total_amount +=
                        (Number(g.amount) || Number(g.quantity) || 0) * (Number(g.unit_price) || 0);
                    entry.document_count++;
                });
            });
            return Object.values(groceryMap)
                .map((item) => ({
                    ...item,
                    avg_unit_price:
                        item.total_quantity > 0 ? item.total_amount / item.total_quantity : 0,
                }))
                .sort((a, b) => (a.grocery_name || "").localeCompare(b.grocery_name || ""));
        },
        aggregatedItems() {
            if (!this.filters.groceryCode) return [];
            const result = { total_quantity: 0, total_amount: 0, document_count: 0 };
            this.aggregationBaseDocuments.forEach((doc) => {
                (doc.groceries || []).forEach((g) => {
                    if (g.code === this.filters.groceryCode.code) {
                        result.total_quantity += Number(g.quantity) || Number(g.amount) || 0;
                        result.total_amount +=
                            (Number(g.amount) || Number(g.quantity) || 0) * (Number(g.unit_price) || 0);
                        result.document_count++;
                    }
                });
            });
            if (result.document_count === 0) return [];
            result.grocery_name = this.filters.groceryCode.name;
            result.grocery_code = this.filters.groceryCode.code;
            result.measure = this.filters.groceryCode.measure;
            result.avg_unit_price =
                result.total_quantity > 0 ? result.total_amount / result.total_quantity : 0;
            return [result];
        },
        activeAggregatedItems() {
            const base = this.filters.groceryCode ? this.aggregatedItems : this.allAggregatedItems;
            return this.customSearch(base, this.aggregateHeaders, this.filters.search);
        },
        expandedSubHeaders() {
            return [
                { text: this.$t("warehouse.document_number"), value: "number" },
                { text: this.$t("warehouse.waybill_date").split(" ")[0] || this.$t("date"), value: "document_date" },
                { text: this.$t("routes.warehouse"), value: "warehouse.name" },
                { text: this.$t("quantity"), value: "grocery_quantity", align: "right" },
                { text: this.$t("value"), value: "grocery_value", align: "right" },
                { text: this.$t("actions"), value: "actions", sortable: false, align: "right" },
            ];
        },
        aggregateHeaders() {
            return [
                { text: this.$t("code"), value: "grocery_code", width: "100px" },
                { text: this.$t("warehouse.product"), value: "grocery_name" },
                {
                    text: this.$t("unit_of_measurment"),
                    value: "measure",
                    width: "140px",
                    align: "center",
                },
                {
                    text: this.$t("warehouse.total_quantity"),
                    value: "total_quantity",
                    align: "right",
                    width: "160px",
                },
                {
                    text: this.$t("warehouse.document_count"),
                    value: "document_count",
                    align: "center",
                    width: "160px",
                },
                { text: "", value: "data-table-expand", sortable: false, width: "40px" },
            ];
        },
        exportHeaders() {
            if (this.aggregateMode) return this.aggregateHeaders.filter((h) => h.value !== "data-table-expand");
            return this.headers.filter((h) => h.value !== "actions");
        },
        exportItems() {
            if (this.aggregateMode) {
                return this.activeAggregatedItems.map((item) => ({
                    ...item,
                    total_quantity: this.formatNumber(item.total_quantity),
                }));
            }
            return this.displayedDocuments;
        },
    },
    methods: {
        getDocumentsForGroceryWithDetails(groceryCode) {
            return this.aggregationBaseDocuments
                .filter((doc) => (doc.groceries || []).some((g) => g.code === groceryCode))
                .map((doc) => {
                    const grocery = (doc.groceries || []).find((g) => g.code === groceryCode);
                    const quantity = Number(grocery?.quantity) || Number(grocery?.amount) || 0;
                    const unitPrice = Number(grocery?.unit_price) || 0;
                    return {
                        ...doc,
                        grocery_quantity: quantity,
                        grocery_measure: grocery?.measure || "",
                        grocery_value: quantity * unitPrice,
                    };
                });
        },
        getDocumentsForGrocery(groceryCode) {
            return this.aggregationBaseDocuments.filter((doc) =>
                (doc.groceries || []).some((g) => g.code === groceryCode)
            );
        },
        formatNumber(val) {
            if (!val) return "";
            return Number(val).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        },
        setFiltersToLastEntry() {
            if (!this.documents.length) return;
            const sorted = [...this.documents].sort(
                (a, b) => new Date(b.document_date) - new Date(a.document_date)
            );
            const date = sorted[0].document_date.split("T")[0];
            this.$store.commit("warehouse/SET_DATE", [date, date]);
        },
        async load() {
            this.loading = true;
            try {
                const res = await warehouseService.getWarehouseDocuments({
                    document_type: this.documentType,
                });
                this.documents = res.data;
            } catch (e) {
                this.showErrorSnackbar(this.$t("error_loading_documents"));
            } finally {
                this.loading = false;
            }
        },
    },
    watch: {
        "$store.state.warehouse.refreshTrigger"() {
            this.load();
        },
        isExcelDownloading() {
            if (this.isExcelDownloading) {
                this.downloadExcel({
                    fileName: this.exportTitle + ".xlsx",
                    sheets: [
                        {
                            name: this.exportTitle,
                            headers: this.exportHeaders,
                            items: this.exportItems,
                            title: this.exportTitle,
                            subtitle: this.subtitle,
                        },
                    ],
                });
            }
        },
        isPrinting(isPrinting) {
            if (isPrinting) {
                this.printDocument({
                    title: this.exportTitle,
                    subtitle: this.subtitle,
                    tables: [
                        {
                            headers: this.exportHeaders,
                            items: this.exportItems,
                        },
                    ],
                });
            }
        },
    },
};
